import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, UserCog, Ban, Coins, History, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'

type UserStatus = 'active' | 'suspended'
type FilterType = 'all' | 'active' | 'suspended' | 'agent'
type ConfirmAction = null | 'suspend' | 'balance'

interface MockUser {
  id: string
  name: string
  tgId: string
  balance: number
  status: UserStatus
  agent: boolean
  joinDate: string
  hands: number
  totalDeposit: number   // cents
  totalWithdraw: number  // cents
}

const initialUsers: MockUser[] = [
  { id: 'U001', name: '小明', tgId: '@xiaoming', balance: 125000, status: 'active', agent: false, joinDate: '2025-11-15', hands: 1247, totalDeposit: 350000, totalWithdraw: 180000 },
  { id: 'U002', name: 'Sarah_HK', tgId: '@sarah_hk', balance: 88500, status: 'active', agent: true, joinDate: '2025-12-01', hands: 892, totalDeposit: 200000, totalWithdraw: 120000 },
  { id: 'U003', name: '大衛王', tgId: '@david_w', balance: 0, status: 'suspended', agent: false, joinDate: '2026-01-10', hands: 156, totalDeposit: 50000, totalWithdraw: 50000 },
  { id: 'U004', name: 'Lucky88', tgId: '@lucky88', balance: 340200, status: 'active', agent: false, joinDate: '2025-10-20', hands: 3421, totalDeposit: 800000, totalWithdraw: 500000 },
  { id: 'U005', name: '阿花', tgId: '@ahua_tw', balance: 52300, status: 'active', agent: false, joinDate: '2026-01-25', hands: 67, totalDeposit: 80000, totalWithdraw: 15000 },
  { id: 'U006', name: 'PokerKing', tgId: '@pokerking', balance: 215000, status: 'active', agent: true, joinDate: '2025-09-05', hands: 5678, totalDeposit: 1200000, totalWithdraw: 950000 },
]

const filters: { id: FilterType; label: string }[] = [
  { id: 'all', label: '全部' },
  { id: 'active', label: '活躍' },
  { id: 'suspended', label: '停用' },
  { id: 'agent', label: '代理' },
]

const avatarColors = [
  'bg-gradient-to-br from-blue-500 to-blue-700',
  'bg-gradient-to-br from-emerald-500 to-emerald-700',
  'bg-gradient-to-br from-purple-500 to-purple-700',
  'bg-gradient-to-br from-amber-500 to-amber-700',
  'bg-gradient-to-br from-rose-500 to-rose-700',
  'bg-gradient-to-br from-cyan-500 to-cyan-700',
]

function formatAmount(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function getInitials(name: string): string {
  return name.slice(0, 2).toUpperCase()
}

export function AdminUsersScreen() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [selectedUser, setSelectedUser] = useState<MockUser | null>(null)
  const [users, setUsers] = useState<MockUser[]>(initialUsers)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [balanceType, setBalanceType] = useState<'credit' | 'debit'>('credit')
  const [balanceAmount, setBalanceAmount] = useState('')
  const [balanceReason, setBalanceReason] = useState('')

  const filtered = users.filter((u) => {
    const matchesSearch =
      search === '' ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.id.toLowerCase().includes(search.toLowerCase()) ||
      u.tgId.toLowerCase().includes(search.toLowerCase())
    const matchesFilter =
      activeFilter === 'all' ||
      (activeFilter === 'active' && u.status === 'active') ||
      (activeFilter === 'suspended' && u.status === 'suspended') ||
      (activeFilter === 'agent' && u.agent)
    return matchesSearch && matchesFilter
  })

  const resetConfirmState = () => {
    setConfirmAction(null)
    setSuspendReason('')
    setBalanceType('credit')
    setBalanceAmount('')
    setBalanceReason('')
  }

  const closeDrawer = () => {
    setSelectedUser(null)
    resetConfirmState()
  }

  const handleSuspendConfirm = () => {
    if (!selectedUser || !suspendReason.trim()) return
    setUsers((prev) =>
      prev.map((u) =>
        u.id === selectedUser.id ? { ...u, status: 'suspended' as UserStatus } : u
      )
    )
    closeDrawer()
  }

  const handleBalanceConfirm = () => {
    if (!selectedUser || !balanceAmount || !balanceReason.trim()) return
    const amountCents = Math.round(parseFloat(balanceAmount) * 100)
    if (isNaN(amountCents) || amountCents <= 0) return
    setUsers((prev) =>
      prev.map((u) =>
        u.id === selectedUser.id
          ? { ...u, balance: balanceType === 'credit' ? u.balance + amountCents : Math.max(0, u.balance - amountCents) }
          : u
      )
    )
    closeDrawer()
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Search Bar */}
      <motion.div variants={fadeInUp} className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋用戶 (名稱、ID、Telegram)"
            className="w-full h-11 pl-10 pr-4 bg-surface-elevated border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>
      </motion.div>

      {/* Filter Chips */}
      <motion.div variants={fadeInUp} className="px-4 pb-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={cn(
                'flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-all border',
                activeFilter === f.id
                  ? 'bg-gold/20 text-gold border-gold/30'
                  : 'bg-surface-elevated text-muted-foreground border-foreground/5 hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Results Count */}
      <div className="px-5 pb-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
          {filtered.length} 位用戶
        </p>
      </div>

      {/* User List */}
      <motion.div variants={staggerContainer} className="flex-1 overflow-y-auto px-4 pb-8 space-y-2.5 scrollbar-hide">
        {filtered.map((user, i) => (
          <motion.button
            key={user.id}
            variants={cardReveal}
            onClick={() => { setSelectedUser(user); resetConfirmState() }}
            className="w-full text-left p-4 rounded-2xl glass-card active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3">
              <div className={cn('w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0', avatarColors[i % avatarColors.length])}>
                <span className="text-sm font-bold text-white">{getInitials(user.name)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-foreground truncate">{user.name}</span>
                  {user.agent && (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-500/25">
                      <UserCog className="w-3 h-3 text-purple-400" />
                      <span className="text-[10px] font-bold text-purple-400">代理</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground">{user.tgId}</span>
                  <span className="text-[10px] text-muted-foreground/60">{user.id}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold font-mono text-foreground">${formatAmount(user.balance)}</p>
                <div className="flex items-center justify-end gap-1.5 mt-0.5">
                  <span className={cn('w-1.5 h-1.5 rounded-full', user.status === 'active' ? 'bg-emerald-400' : 'bg-red-400')} />
                  <span className={cn('text-[10px] font-medium', user.status === 'active' ? 'text-emerald-400' : 'text-red-400')}>
                    {user.status === 'active' ? '活躍' : '停用'}
                  </span>
                  <span className="text-[10px] text-muted-foreground/50 ml-1">{user.joinDate}</span>
                </div>
              </div>
            </div>
          </motion.button>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">找不到符合條件的用戶</p>
          </div>
        )}
      </motion.div>

      {/* User Detail Drawer */}
      <Drawer.Root open={selectedUser !== null} onOpenChange={(open) => { if (!open) closeDrawer() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              {selectedUser && (
                <div className="px-6 pb-8 safe-area-bottom">
                  <AnimatePresence mode="wait">
                    {/* Suspend Confirmation */}
                    {confirmAction === 'suspend' && (
                      <motion.div
                        key="suspend"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-rose-400" />
                            <h3 className="text-base font-bold text-rose-400">確定要停用此帳號？</h3>
                          </div>
                          <p className="text-xs text-rose-300/80">此操作將立即阻止用戶登入及遊戲</p>
                          <p className="text-xs text-muted-foreground">用戶: {selectedUser.name} ({selectedUser.id})</p>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">停用原因 *</label>
                          <textarea
                            value={suspendReason}
                            onChange={(e) => setSuspendReason(e.target.value)}
                            placeholder="請輸入停用原因..."
                            className="w-full h-24 p-3 rounded-xl bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-rose-500/40"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={resetConfirmState}
                            className="h-12 glass-card rounded-2xl font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleSuspendConfirm}
                            disabled={!suspendReason.trim()}
                            className={cn(
                              'h-12 rounded-2xl font-bold text-white transition-opacity',
                              suspendReason.trim() ? 'bg-rose-600' : 'bg-rose-600/40 opacity-50 cursor-not-allowed'
                            )}
                          >
                            確認停用
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Balance Adjustment */}
                    {confirmAction === 'balance' && (
                      <motion.div
                        key="balance"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        <Drawer.Title className="text-lg font-bold text-foreground">調整餘額</Drawer.Title>
                        <p className="text-xs text-muted-foreground">
                          用戶: {selectedUser.name} | 當前餘額: ${formatAmount(selectedUser.balance)}
                        </p>

                        {/* Credit / Debit Radio */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => setBalanceType('credit')}
                            className={cn(
                              'flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all',
                              balanceType === 'credit'
                                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                : 'bg-surface-elevated border-foreground/10 text-muted-foreground'
                            )}
                          >
                            增加 (Credit)
                          </button>
                          <button
                            onClick={() => setBalanceType('debit')}
                            className={cn(
                              'flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all',
                              balanceType === 'debit'
                                ? 'bg-rose-500/15 border-rose-500/30 text-rose-400'
                                : 'bg-surface-elevated border-foreground/10 text-muted-foreground'
                            )}
                          >
                            扣除 (Debit)
                          </button>
                        </div>

                        {/* Amount Input */}
                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">金額 (USD)</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={balanceAmount}
                              onChange={(e) => setBalanceAmount(e.target.value)}
                              placeholder="0.00"
                              className="w-full h-11 pl-8 pr-4 bg-surface-elevated border border-foreground/10 rounded-xl text-sm text-foreground font-mono placeholder:text-muted-foreground/50 focus:outline-none focus:border-gold/50"
                            />
                          </div>
                        </div>

                        {/* Reason */}
                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">原因 *</label>
                          <textarea
                            value={balanceReason}
                            onChange={(e) => setBalanceReason(e.target.value)}
                            placeholder="請輸入調整原因..."
                            className="w-full h-20 p-3 rounded-xl bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-gold/40"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={resetConfirmState}
                            className="h-12 glass-card rounded-2xl font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleBalanceConfirm}
                            disabled={!balanceAmount || !balanceReason.trim()}
                            className={cn(
                              'h-12 rounded-2xl font-bold text-white transition-opacity',
                              balanceAmount && balanceReason.trim() ? 'bg-gold' : 'bg-gold/40 opacity-50 cursor-not-allowed'
                            )}
                          >
                            確認調整
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Default Drawer Content */}
                    {confirmAction === null && (
                      <motion.div
                        key="default"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {/* User Header */}
                        <div className="flex items-center gap-4 mb-6">
                          <div className={cn(
                            'w-14 h-14 rounded-full flex items-center justify-center',
                            avatarColors[users.indexOf(selectedUser) % avatarColors.length]
                          )}>
                            <span className="text-lg font-bold text-white">{getInitials(selectedUser.name)}</span>
                          </div>
                          <div className="flex-1">
                            <Drawer.Title className="text-lg font-bold text-foreground">{selectedUser.name}</Drawer.Title>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-muted-foreground">{selectedUser.tgId}</span>
                              <span className={cn(
                                'px-2 py-0.5 rounded-full text-[10px] font-bold',
                                selectedUser.status === 'active'
                                  ? 'bg-emerald-500/15 text-emerald-400'
                                  : 'bg-red-500/15 text-red-400'
                              )}>
                                {selectedUser.status === 'active' ? '活躍' : '停用'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-3 gap-2.5 mb-6">
                          {[
                            { label: '餘額', value: `$${formatAmount(selectedUser.balance)}`, color: 'text-foreground' },
                            { label: '總存款', value: `$${formatAmount(selectedUser.totalDeposit)}`, color: 'text-emerald-400' },
                            { label: '總提款', value: `$${formatAmount(selectedUser.totalWithdraw)}`, color: 'text-rose-400' },
                            { label: '總手數', value: selectedUser.hands.toLocaleString(), color: 'text-foreground' },
                            { label: '淨存提差', value: `$${formatAmount(selectedUser.totalDeposit - selectedUser.totalWithdraw)}`, color: selectedUser.totalDeposit >= selectedUser.totalWithdraw ? 'text-emerald-400' : 'text-rose-400' },
                            { label: '加入日期', value: selectedUser.joinDate, color: 'text-muted-foreground' },
                          ].map((item) => (
                            <div key={item.label} className="p-3 rounded-xl bg-surface-elevated text-center">
                              <p className="text-[10px] text-muted-foreground mb-1">{item.label}</p>
                              <p className={cn('text-sm font-bold font-mono', item.color)}>{item.value}</p>
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-2">
                          <button
                            onClick={() => setConfirmAction('suspend')}
                            className="w-full h-12 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 font-bold text-sm flex items-center justify-center gap-2"
                          >
                            <Ban className="w-4 h-4" />
                            停用帳號
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => setConfirmAction('balance')}
                              className="h-12 rounded-2xl bg-gold/10 border border-gold/20 text-gold font-bold text-sm flex items-center justify-center gap-2"
                            >
                              <Coins className="w-4 h-4" />
                              調整餘額
                            </button>
                            <button
                              onClick={() => { closeDrawer(); navigate(`/admin/transactions?userId=${selectedUser.id}&userName=${encodeURIComponent(selectedUser.name)}`) }}
                              className="h-12 rounded-2xl glass-card text-foreground font-bold text-sm flex items-center justify-center gap-2"
                            >
                              <History className="w-4 h-4" />
                              查看紀錄
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
